'use client'

/**
 * CryptoBoard — top coins by market cap with live-refreshing price and 24h/7d change. Fed by
 * getCryptoPrices() (CoinGecko), re-polls every 30s. Informational only.
 */

import { useEffect, useState } from 'react'
import type { CryptoBoard as CryptoBoardData } from '@/lib/dashboard/cryptoPrices'

const fmtPrice = (n: number) =>
  n >= 1000 ? n.toLocaleString('en-US', { maximumFractionDigits: 0 })
    : n >= 1 ? n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 6 })
const fmtBig = (n: number) =>
  n >= 1e12 ? `$${(n / 1e12).toFixed(2)}T` : n >= 1e9 ? `$${(n / 1e9).toFixed(1)}B` : n >= 1e6 ? `$${(n / 1e6).toFixed(0)}M` : `$${n.toLocaleString()}`

function Pct({ v }: { v: number | null }) {
  if (v == null) return <span className="text-zinc-600">—</span>
  return <span className={`tabular-nums font-semibold ${v >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>{v >= 0 ? '+' : ''}{v.toFixed(1)}%</span>
}

export default function CryptoBoard({ initial }: { initial: CryptoBoardData }) {
  const [data, setData] = useState<CryptoBoardData>(initial)

  useEffect(() => {
    let alive = true
    const pull = () => fetch('/api/dashboard/crypto', { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null)).then((j) => { if (alive && j?.coins?.length) setData(j) }).catch(() => {})
    const id = setInterval(() => { if (!document.hidden) pull() }, 30000)
    return () => { alive = false; clearInterval(id) }
  }, [])

  if (!data.coins.length) {
    return <div className="rounded-2xl border border-zinc-800/70 bg-zinc-900/30 px-4 py-12 text-center text-sm text-zinc-500">Loading crypto prices…</div>
  }

  return (
    <div className="overflow-x-auto rounded-2xl border border-zinc-800/70 bg-zinc-900/30">
      <table className="w-full min-w-[560px] text-sm">
        <thead>
          <tr className="border-b border-zinc-800/70 text-[11px] uppercase tracking-wide text-zinc-500">
            <th className="px-3 py-2 text-left font-semibold">#</th>
            <th className="px-3 py-2 text-left font-semibold">Coin</th>
            <th className="px-3 py-2 text-right font-semibold">Price</th>
            <th className="px-3 py-2 text-right font-semibold">24h</th>
            <th className="px-3 py-2 text-right font-semibold">7d</th>
            <th className="px-3 py-2 text-right font-semibold">Market Cap</th>
            <th className="hidden px-3 py-2 text-right font-semibold sm:table-cell">24h Volume</th>
          </tr>
        </thead>
        <tbody>
          {data.coins.map((c) => (
            <tr key={c.id} className="border-t border-zinc-800/50 transition-colors hover:bg-zinc-800/30">
              <td className="px-3 py-2 text-[12px] tabular-nums text-zinc-600">{c.rank ?? '—'}</td>
              <td className="px-3 py-2">
                <span className="flex items-center gap-2">
                  {c.image && /* eslint-disable-next-line @next/next/no-img-element */ <img src={c.image} alt="" width={22} height={22} className="h-[22px] w-[22px] rounded-full" loading="lazy" />}
                  <span className="font-bold text-white">{c.name}</span>
                  <span className="text-[11px] font-semibold text-zinc-500">{c.symbol}</span>
                </span>
              </td>
              <td className="px-3 py-2 text-right font-bold tabular-nums text-white">${fmtPrice(c.price)}</td>
              <td className="px-3 py-2 text-right"><Pct v={c.change24hPct} /></td>
              <td className="px-3 py-2 text-right"><Pct v={c.change7dPct} /></td>
              <td className="px-3 py-2 text-right tabular-nums text-zinc-300">{fmtBig(c.marketCap)}</td>
              <td className="hidden px-3 py-2 text-right tabular-nums text-zinc-400 sm:table-cell">{fmtBig(c.volume24h)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
